import React, { useEffect, useState } from 'react'

const Donation = () => {
	const [amount, setAmount] = useState(0)
	const [token, setToken] = useState('')
	const [sponsorData, setSponsorData] = useState(null)
	const [loading, setLoading] = useState(false)

	const [status, setStatus] = useState('loading')
	const [message, setMessage] = useState('')

	useEffect(() => {
		const query = window.location.search
		const params = new URLSearchParams(query)

		const tkn = params.get('token')
		const data = params.get('data')

		if (tkn) setToken(tkn)

		if (data) {
			try {
				const parsed = JSON.parse(decodeURIComponent(data))

				if (typeof parsed !== 'object' || parsed === null) {
					throw new Error('Parsed data is not a valid object')
				}

				console.log('✅ Incoming Data:', parsed)

				setSponsorData(parsed)
				setAmount(parsed?.amount || 0)

				setStatus('ready')
			} catch (err) {
				console.error('❌ Parse Error:', err.message || err)
				setStatus('failed')
				setMessage('Invalid payment data provided. Please check the link and try again.')
			}
		} else {
			setStatus('failed')
			setMessage('Missing payment information in the URL.')
		}
	}, [])

	const handlePayment = async () => {
		if (!token || !sponsorData) {
			setStatus('failed')
			setMessage('Missing authorization or payment details.')
			return
		}

		if (!window.Razorpay) {
			setStatus('failed')
			setMessage(
				'Payment gateway failed to load. Please disable ad-blockers and check your connection.',
			)
			return
		}

		setLoading(true)
		setStatus('processing')

		try {
			const response = await fetch(`${process.env.REACT_APP_DONATION_API}/payment`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${token}`,
				},
				body: JSON.stringify({
					amount: sponsorData.amount * 100,
					currency: 'INR',
				}),
			})

			if (!response.ok) {
				throw new Error(`Server returned status: ${response.status}`)
			}

			const resData = await response.json()
			const order = resData?.data

			if (!order?.id) {
				setStatus('failed')
				setMessage(resData?.message || 'Unable to create payment order. Please try again.')
				return
			}

			const options = {
				key: order.apikey,
				amount: order.amount,
				currency: order.currency,
				name: order.appname || 'Donation',
				description: 'Payment',
				image: order.logo,
				order_id: order.id,
				redirect: false,

				handler: async function(paymentData) {
					try {
						setStatus('processing')

						let payload = {
							...paymentData,
							amount: sponsorData.amount,
							user: sponsorData.user,
						}

						if (sponsorData.upgradePremium) {
							payload = {
								...payload,
								upgradePremium: true,
								premiumtype: sponsorData.premiumtype,
								isSponsor: false,
							}
						} else {
							payload = {
								...payload,
								image: sponsorData.image,
								hub: sponsorData.hub,
								sanstha: sponsorData.sanstha,
								isSponsor: true,
							}
						}

						const verifyRes = await fetch(`${process.env.REACT_APP_DONATION_API}/payment/verify`, {
							method: 'POST',
							headers: {
								'Content-Type': 'application/json',
								Authorization: `Bearer ${token}`,
							},
							body: JSON.stringify(payload),
						})

						if (!verifyRes.ok) {
							throw new Error(`Verification request failed with status: ${verifyRes.status}`)
						}

						const verifyData = await verifyRes.json()

						if (verifyData.success) {
							setStatus('success')
							setMessage(
								'Your payment was successfully completed. Please close this window. Thank you!',
							)

							if (sponsorData.pushnotificationToken) {
								try {
									const action = 'razorepayAuth'
									const pushRes = await fetch(
										`${process.env.REACT_APP_DONATION_API}/auth/razorpay`,
										{
											method: 'POST',
											headers: {
												'Content-Type': 'application/json',
											},
											body: JSON.stringify({
												pushnotificationToken: sponsorData?.pushnotificationToken,
												isPremium: sponsorData?.upgradePremium === true ? 'true' : 'false',
												action,
											}),
										},
									)

									if (!pushRes.ok) {
										console.warn(`⚠️ Push notification failed with status: ${pushRes.status}`)
									}

									console.log('✅ Push notification sent successfully')
								} catch (err) {
									console.error('❌ Push API Error:', err.message || err)
								}
							}
						} else {
							setStatus('failed')
							setMessage(
								verifyData?.message ||
									'Payment verification failed. If money was deducted, it will be refunded.',
							)
						}
					} catch (err) {
						console.error('❌ Verify Error:', err.message || err)
						setStatus('failed')
						setMessage('Something went wrong during payment verification. Please contact support.')
					}
				},

				prefill: {
					name: `${sponsorData?.name || 'User'}`,
					email: '',
					contact: `${sponsorData?.mobileNumber || ''}`,
				},

				theme: {
					color: '#2563EB',
				},

				modal: {
					ondismiss: function() {
						setStatus('failed')
						setMessage('Payment was cancelled by the user.')
					},
				},
			}

			const rzp = new window.Razorpay(options)
			rzp.open()

			rzp.on('payment.failed', function(response) {
				console.error('❌ Razorpay Event Error:', response.error)
				setStatus('failed')
				setMessage(response.error?.description || 'Payment failed. Please try again.')
			})
		} catch (err) {
			console.error('❌ Payment Error:', err.message || err)
			setStatus('failed')
			setMessage(
				'Something went wrong while initiating payment. Please check your connection and try again.',
			)
		} finally {
			setLoading(false)
		}
	}

	return (
		<div style={styles.wrapper}>
			<style>
				{`
                    * {
                        box-sizing: border-box;
                        font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                    }
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                    @keyframes slideUpFade {
                        0% { opacity: 0; transform: translateY(20px); }
                        100% { opacity: 1; transform: translateY(0); }
                    }
                    @keyframes scaleIn {
                        0% { opacity: 0; transform: scale(0.8); }
                        100% { opacity: 1; transform: scale(1); }
                    }
                    .pay-btn {
                        background-color: #2563EB;
                        color: #ffffff;
                        transition: all 0.2s ease-in-out;
                        box-shadow: 0 4px 6px -1px rgba(37, 99, 235, 0.2);
                    }
                    .pay-btn:hover:not(:disabled) {
                        background-color: #1D4ED8;
                        transform: translateY(-1px);
                        box-shadow: 0 6px 8px -1px rgba(37, 99, 235, 0.3);
                    }
                    .pay-btn:active:not(:disabled) {
                        transform: translateY(0);
                    }
                    .pay-btn:disabled {
                        background-color: #93C5FD;
                        cursor: not-allowed;
                        box-shadow: none;
                    }
                `}
			</style>

			<div style={styles.card}>
				{status === 'loading' && (
					<div style={styles.content}>
						<div style={{ ...styles.iconContainer, color: '#9CA3AF' }}>
							<div style={styles.spinner}></div>
						</div>
						<h2 style={styles.heading}>Loading Details</h2>
						<p style={styles.text}>Preparing your secure checkout...</p>
					</div>
				)}

				{status === 'ready' && (
					<div style={styles.content}>
						<div
							style={{
								...styles.iconContainer,
								backgroundColor: '#EFF6FF',
								color: '#2563EB',
								padding: '16px',
								borderRadius: '50%',
							}}
						>
							<svg
								width="40"
								height="40"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								viewBox="0 0 24 24"
								strokeLinecap="round"
								strokeLinejoin="round"
							>
								<rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
								<line x1="1" y1="10" x2="23" y2="10"></line>
							</svg>
						</div>
						<h2 style={styles.heading}>Complete Your Payment {sponsorData?.name || 'User'}</h2>
						<p style={styles.text}>
							You are about to make a secure payment. Click the button below to proceed.
						</p>

						<div style={styles.amountBox}>
							<span style={styles.currencySymbol}>₹</span>
							<span style={styles.amountText}>{amount}</span>
						</div>

						<button
							className="pay-btn"
							style={styles.payButton}
							onClick={handlePayment}
							disabled={loading}
						>
							{loading ? 'Processing...' : `Pay Securely`}
						</button>
					</div>
				)}

				{status === 'processing' && (
					<div style={styles.content}>
						<div style={styles.iconContainer}>
							<div style={styles.spinner}></div>
						</div>
						<h2 style={styles.heading}>Processing Payment</h2>
						<p style={styles.text}>
							Please wait while we confirm your transaction. Do not close or refresh this page.
						</p>
					</div>
				)}

				{status === 'success' && (
					<div style={{ ...styles.content, animation: 'scaleIn 0.4s ease-out' }}>
						<div style={{ ...styles.iconContainer, color: '#10B981' }}>
							<svg
								width="64"
								height="64"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								viewBox="0 0 24 24"
								strokeLinecap="round"
								strokeLinejoin="round"
							>
								<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
								<polyline points="22 4 12 14.01 9 11.01"></polyline>
							</svg>
						</div>
						<h2 style={styles.heading}>Payment Successful!</h2>
						<p style={styles.text}>{message}</p>
					</div>
				)}

				{status === 'failed' && (
					<div style={{ ...styles.content, animation: 'scaleIn 0.4s ease-out' }}>
						<div style={{ ...styles.iconContainer, color: '#EF4444' }}>
							<svg
								width="64"
								height="64"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								viewBox="0 0 24 24"
								strokeLinecap="round"
								strokeLinejoin="round"
							>
								<circle cx="12" cy="12" r="10"></circle>
								<line x1="15" y1="9" x2="9" y2="15"></line>
								<line x1="9" y1="9" x2="15" y2="15"></line>
							</svg>
						</div>
						<h2 style={styles.heading}>Payment Failed</h2>
						<p style={styles.text}>{message}</p>
						<button
							className="pay-btn"
							style={{ ...styles.payButton, marginTop: '24px', backgroundColor: '#4B5563' }}
							onClick={() => window.location.reload()}
						>
							Try Again
						</button>
					</div>
				)}
			</div>
		</div>
	)
}

const styles = {
	wrapper: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		minHeight: '100vh',
		backgroundColor: '#F3F4F6',
		padding: '20px',
	},
	card: {
		backgroundColor: '#ffffff',
		borderRadius: '24px',
		padding: '48px 32px',
		width: '100%',
		maxWidth: '440px',
		textAlign: 'center',
		boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.01)',
		animation: 'slideUpFade 0.5s ease-out',
	},
	content: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
	},
	iconContainer: {
		marginBottom: '24px',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
	},
	heading: {
		fontSize: '24px',
		fontWeight: '700',
		color: '#111827',
		margin: '0 0 12px 0',
	},
	text: {
		color: '#6B7280',
		fontSize: '15px',
		lineHeight: '1.5',
		margin: '0',
	},
	amountBox: {
		marginTop: '32px',
		marginBottom: '8px',
		padding: '16px 32px',
		backgroundColor: '#F9FAFB',
		borderRadius: '16px',
		border: '1px solid #E5E7EB',
		display: 'flex',
		alignItems: 'baseline',
		justifyContent: 'center',
		gap: '4px',
		width: '100%',
	},
	currencySymbol: {
		fontSize: '20px',
		fontWeight: '600',
		color: '#4B5563',
	},
	amountText: {
		fontSize: '36px',
		fontWeight: '800',
		color: '#111827',
	},
	payButton: {
		marginTop: '24px',
		width: '100%',
		padding: '16px 24px',
		border: 'none',
		borderRadius: '12px',
		fontSize: '16px',
		fontWeight: '600',
		cursor: 'pointer',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		gap: '8px',
	},
	spinner: {
		width: '48px',
		height: '48px',
		border: '4px solid #E5E7EB',
		borderTop: '4px solid #2563EB',
		borderRadius: '50%',
		animation: 'spin 1s cubic-bezier(0.55, 0.15, 0.45, 0.85) infinite',
	},
}

export default Donation
